import React, { useEffect, useState } from 'react'
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore'
import { db } from '../firebaseConfig'
import { useParams, useNavigate } from 'react-router-dom'
import { generateBsPDF } from '../utils/BsPdf'
import ModalPDF from './ModalPDF'
import { DetailBackButton, DetailApprovalActions } from './DetailApprovalActions'
import { toast } from 'react-toastify'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { getEditHistoryEntries } from '../utils/editHistory'

const DetailBs = () => {
    const [userData, setUserData] = useState(null)
    const [bonSementaraDetail, setBonSementaraDetail] = useState(null)
    const [reviewers, setReviewers] = useState([])
    const [, setError] = useState(null)
    const [isLoading, setIsLoading] = useState(false)
    const [isLpjApproved, setIsLpjApproved] = useState(false)
    // Bagian CA: nota yang sudah dicatat lewat "Catat Nota" (CatatNota.jsx)
    // untuk BS ini -- ditawarkan sebagai baris item siap-pakai saat
    // "Buat Laporan" diklik, lihat handleBuatLaporan() di bawah.
    const [notaDraftForBs, setNotaDraftForBs] = useState([])
    // Bagian AN: riwayat edit (siapa mengedit, kapan, alasannya) -- dipakai
    // Super Admin/reviewer untuk lihat berapa kali & kenapa pengajuan ini
    // pernah diperbaiki setelah dibuat.
    const [editHistoryWithNames, setEditHistoryWithNames] = useState([])

    const { id } = useParams()
    const navigate = useNavigate()
    const uid = localStorage.getItem('userUid')

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true)

                // Fetch user data
                const userDocRef = doc(db, 'users', uid)
                const userSnapshot = await getDoc(userDocRef)

                if (!userSnapshot.exists()) {
                    throw new Error('User tidak ditemukan')
                }

                // Fetch lpj data
                const bonSementaraDocRef = doc(db, 'bonSementara', id)
                const bonSementaraSnapshot = await getDoc(bonSementaraDocRef)

                if (!bonSementaraSnapshot.exists()) {
                    throw new Error('Data Bon Sementara tidak ditemukan')
                }

                const bonSementaraData = bonSementaraSnapshot.data()
                setUserData(userSnapshot.data())
                setBonSementaraDetail(bonSementaraData)

                // Helper function to fetch names of reviewers
                const fetchReviewerNames = async (reviewerArray) => {
                    if (!Array.isArray(reviewerArray)) return []
                    const promises = reviewerArray.map(async (reviewerUid) => {
                        try {
                            const reviewerDocRef = doc(db, 'userDirectory', reviewerUid)
                            const reviewerSnapshot = await getDoc(reviewerDocRef)
                            return reviewerSnapshot.exists() ? reviewerSnapshot.data().nama : null
                        } catch (error) {
                            console.error(`Error fetching reviewer (${reviewerUid}):`, error)
                            return null
                        }
                    })
                    return Promise.all(promises)
                }

                // Fetch names for all reviewers in reviewer1 and reviewer2
                const [reviewer1Names, reviewer2Names] = await Promise.all([
                    fetchReviewerNames(bonSementaraData?.user?.reviewer1),
                    fetchReviewerNames(bonSementaraData?.user?.reviewer2),
                ])

                // Combine reviewer names and filter out null values
                const validReviewerNames = [...reviewer1Names, ...reviewer2Names].filter((name) => name !== null)
                setReviewers({
                    reviewerNames: validReviewerNames,
                    reviewer1Names: reviewer1Names.filter((name) => name !== null),
                    reviewer2Names: reviewer2Names.filter((name) => name !== null),
                })
            } catch (error) {
                console.error('Error fetching data:', error)
                setError(error.message)
            } finally {
                setIsLoading(false)
            }
        }

        if (uid && id) {
            fetchData()
        }
    }, [uid, id])

    // Cek apakah BS ini sudah punya LPJ yang Disetujui -- kalau sudah, tombol
    // "Buat Laporan" wajib disabled supaya tidak bisa dibuat LPJ dobel untuk
    // BS yang sama (nomorBS dipakai sebagai kunci pencocokan, sama seperti
    // pola fetchLpjStatus di BsTable.jsx).
    useEffect(() => {
        const checkLpjStatus = async () => {
            if (!bonSementaraDetail || !userData) return
            if (userData.uid !== bonSementaraDetail.user?.uid) return

            const nomorBS = bonSementaraDetail?.bonSementara?.[0]?.nomorBS
            if (!nomorBS) return

            try {
                const lpjSnapshot = await getDocs(
                    query(collection(db, 'lpj'), where('user.uid', '==', userData.uid))
                )
                const existingLpj = lpjSnapshot.docs
                    .map((docSnap) => docSnap.data())
                    .find((d) => d.nomorBS === nomorBS)

                setIsLpjApproved(existingLpj?.status === 'Disetujui')
            } catch (error) {
                console.error('Error checking status LPJ:', error)
            }
        }

        checkLpjStatus()
    }, [bonSementaraDetail, userData])

    // Bagian CA: ambil nota yang sudah dicatat untuk BS ini lewat "Catat
    // Nota" -- ditawarkan sebagai baris item siap-pakai saat "Buat Laporan".
    // Bagian CE koreksi: CatatNota.jsx menyimpan `bsId` per ITEM (format
    // `${docId}__${index}`, karena satu dokumen BS bisa punya beberapa item
    // dengan nomorBS sendiri-sendiri) -- bukan `id` dokumen polos lagi.
    // `handleBuatLaporan()` di bawah cuma pernah menangani item PERTAMA
    // (bonSementara[0]), jadi di sini juga sengaja cuma dicocokkan ke
    // `${id}__0` -- nota milik item ke-2 dst (kalau ada) belum ditawarkan di
    // halaman ini (lihat catatan Bagian CE di SUMMARY_PENGEMBANGAN.md).
    useEffect(() => {
        const fetchNotaDraft = async () => {
            if (!userData || userData.uid !== bonSementaraDetail?.user?.uid) return
            try {
                const snap = await getDocs(
                    query(collection(db, 'notaDraft'), where('user.uid', '==', userData.uid), where('bsId', '==', `${id}__0`))
                )
                setNotaDraftForBs(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
            } catch (error) {
                console.error('Error fetching notaDraft untuk BS ini:', error)
            }
        }
        fetchNotaDraft()
    }, [userData, bonSementaraDetail, id])

    // Bagian AN: resolve nama editor (uid -> nama) untuk tiap entri riwayat
    // edit di statusHistory, supaya tidak cuma tampilkan uid mentah.
    useEffect(() => {
        const resolveEditHistoryNames = async () => {
            const entries = getEditHistoryEntries(bonSementaraDetail?.statusHistory)
            if (entries.length === 0) {
                setEditHistoryWithNames([])
                return
            }

            const withNames = await Promise.all(
                entries.map(async (entry) => {
                    let actorName = 'N/A'
                    try {
                        const actorDocRef = doc(db, 'userDirectory', entry.actor)
                        const actorSnapshot = await getDoc(actorDocRef)
                        if (actorSnapshot.exists()) actorName = actorSnapshot.data().nama
                    } catch (error) {
                        console.error('Error fetching editor name:', error)
                    }
                    return { ...entry, actorName }
                })
            )

            setEditHistoryWithNames(withNames)
        }

        resolveEditHistoryNames()
    }, [bonSementaraDetail])

    // Fungsi untuk mendapatkan status dengan informasi reviewer berikutnya
    const getStatusWithNextReviewer = (bonSementara, reviewerNames) => {
        if (!bonSementara) return { text: 'N/A', hasParenthesis: false }

        const status = bonSementara.status
        const reviewer2Array = bonSementara?.user?.reviewer2 || []
        const reviewer2Exists = Array.isArray(reviewer2Array) && reviewer2Array.some((uid) => uid)

        switch (status) {
            case 'Menunggu Validasi':
                // Menunggu validasi oleh reviewer pertama
                if (reviewerNames.reviewer1Names && reviewerNames.reviewer1Names.length > 0) {
                    return {
                        mainText: 'Menunggu Validasi',
                        parenthesisText: reviewerNames.reviewer1Names.join(', '),
                        hasParenthesis: true
                    }
                }
                return { text: 'Menunggu Validasi', hasParenthesis: false }

            case 'Divalidasi':
                // Sudah divalidasi, menunggu approval dari reviewer 1
                if (reviewerNames.reviewer1Names && reviewerNames.reviewer1Names.length > 0) {
                    return {
                        mainText: 'Divalidasi',
                        parenthesisText: `Menunggu Approval ${reviewerNames.reviewer1Names.join(', ')}`,
                        hasParenthesis: true
                    }
                }
                return { text: 'Divalidasi', hasParenthesis: false }

            case 'Diproses':
                // Sudah di-approve oleh reviewer 1
                if (bonSementara.approvedByReviewer1) {
                    // Jika ada reviewer 2, tampilkan nama reviewer 2
                    if (reviewer2Exists && reviewerNames.reviewer2Names && reviewerNames.reviewer2Names.length > 0) {
                        return {
                            mainText: 'Diproses',
                            parenthesisText: `Menunggu Approval ${reviewerNames.reviewer2Names.join(', ')}`,
                            hasParenthesis: true
                        }
                    }
                    // Jika tidak ada reviewer 2, menunggu Super Admin
                    return {
                        mainText: 'Diproses',
                        parenthesisText: 'Menunggu Approval Super Admin',
                        hasParenthesis: true
                    }
                }
                return { text: 'Diproses', hasParenthesis: false }

            case 'Disetujui':
                return {
                    mainText: 'Disetujui',
                    parenthesisText: 'Selesai',
                    hasParenthesis: true
                }

            case 'Ditolak':
                return { text: 'Ditolak', hasParenthesis: false }

            case 'Dibatalkan':
                return { text: 'Dibatalkan', hasParenthesis: false }

            default:
                return { text: status || 'N/A', hasParenthesis: false }
        }
    }

    // Fungsi untuk render status dengan styling
    const renderStatus = (statusObj) => {
        if (!statusObj.hasParenthesis) {
            return statusObj.text
        }
        return (
            <>
                {statusObj.mainText} <span className="text-green-600 font-bold">({statusObj.parenthesisText})</span>
            </>
        )
    }

    // Fungsi untuk mendapatkan status approval dengan nama reviewer
    const getDetailedApprovalStatus = (bonSementara, reviewerNames) => {
        if (!bonSementara || !bonSementara.statusHistory || bonSementara.statusHistory.length === 0) {
            return '-'
        }

        const lastStatus = bonSementara.statusHistory[bonSementara.statusHistory.length - 1]
        const { status, actor } = lastStatus

        // Helper function to determine approver
        const determineApprover = (reviewerArray, roleIndexStart) => {
            const reviewerIndex = reviewerArray.findIndex((uid) => uid === actor)
            if (reviewerIndex !== -1 && reviewerNames.reviewerNames) {
                return reviewerNames.reviewerNames[roleIndexStart + reviewerIndex] || 'N/A'
            }
            return '-'
        }

        const reviewer1Array = bonSementara?.user?.reviewer1 || []
        const reviewer2Array = bonSementara?.user?.reviewer2 || []
        const reviewer2Exists = Array.isArray(reviewer2Array) && reviewer2Array.some((uid) => uid)

        // Cek status approval dari reviewer
        if (bonSementara.approvedByReviewer1Status === 'reviewer' && bonSementara.approvedByReviewer1) {
            const reviewer1 = determineApprover(reviewer1Array, 0)
            if (reviewer1 !== '-') return reviewer1
        }

        switch (bonSementara.status) {
            case 'Ditolak': {
                if (status.includes('Super Admin')) {
                    return 'Super Admin'
                }
                if (status.includes('Reviewer 1')) {
                    return determineApprover(reviewer1Array, 0)
                }
                if (status.includes('Reviewer 2')) {
                    return determineApprover(reviewer2Array, reviewer1Array.length)
                }
                break
            }

            case 'Diproses': {
                if (bonSementara.approvedBySuperAdmin) {
                    return 'Super Admin'
                }
                if (bonSementara.approvedByReviewer1) {
                    return determineApprover(reviewer1Array, 0)
                }
                break
            }

            case 'Disetujui': {
                if (status.includes('Super Admin')) {
                    return 'Super Admin'
                }
                if (!reviewer2Exists && status.includes('Reviewer 1')) {
                    return determineApprover(reviewer1Array, 0)
                }
                if (status.includes('Reviewer 2')) {
                    return determineApprover(reviewer2Array, reviewer1Array.length)
                }
                break
            }

            default:
                return '-'
        }

        return '-'
    }

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A'
        const date = new Date(dateString)
        return new Intl.DateTimeFormat('id-ID', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        }).format(date)
    }

    const [modalPdfUrl, setModalPdfUrl] = useState(null)
    const [modalTitle, setModalTitle] = useState('')

    const closePreview = () => {
        setModalPdfUrl(null)
        setModalTitle('')
    }

    const handleGenerateAndPreviewPDF = async () => {
        setIsLoading(true)
        try {
            setIsLoading(true)
            const url = await generateBsPDF(bonSementaraDetail)

            if (url) {
                setModalPdfUrl(url)
                setModalTitle(`Preview ${bonSementaraDetail.displayId}`)
            }
        } catch (error) {
            toast.error('Gagal menghasilkan PDF')
            console.error(error)
        } finally {
            setIsLoading(false)
        }
    }

    const handleBuatLaporan = () => {
        const bonSementara = bonSementaraDetail?.bonSementara?.[0]
        const userDetail = bonSementaraDetail?.user
        // Bagian CA: nota yang sudah dicatat di "Catat Nota" untuk BS ini
        // ikut dibawa sebagai baris item siap-pakai di form LPJ -- lihat
        // prefillLpjItems/prefillNotaDraftIds di FormLpjUmum.jsx/
        // FormLpjMarketing.jsx.
        // Bagian CC: field disamakan dengan skema nota di CatatNota.jsx
        // (isLainnya/namaItem/jenisLain/biaya/jumlah/jumlahBiaya/plat), bukan
        // nominal/kategori bebas teks lagi.
        const prefillLpjItems = notaDraftForBs.map((n) => ({
            isLainnya: n.isLainnya, namaItem: n.namaItem, jenisLain: n.jenisLain,
            biaya: n.biaya, jumlah: n.jumlah, jumlahBiaya: n.jumlahBiaya, plat: n.plat, keterangan: n.keterangan
        }))
        const prefillNotaDraftIds = notaDraftForBs.map((n) => n.id)
        if (bonSementara?.kategori === 'GA/Umum') {
            navigate('/lpj/umum', {
                state: {
                    nomorBS: bonSementara.nomorBS,
                    jumlahBS: bonSementara.jumlahBS,
                    aktivitas: bonSementara.aktivitas,
                    unit: userDetail.unit,
                    prefillLpjItems,
                    prefillNotaDraftIds
                }
            })
        } else if (bonSementara?.kategori === 'Marketing/Operasional') {
            navigate('/lpj/marketing', {
                state: {
                    nomorBS: bonSementara.nomorBS,
                    jumlahBS: bonSementara.jumlahBS,
                    aktivitas: bonSementara.aktivitas,
                    unit: userDetail.unit,
                    prefillLpjItems,
                    prefillNotaDraftIds
                }
            })
        } else {
            alert('Kategori tidak dikenali.')
        }
    }

    if (!userData || !bonSementaraDetail) {
        return (
            <div className="container mx-auto py-10 px-4 md:py-8 md:px-0">
                <h2 className="text-xl font-medium mb-4 dark:text-gray-100">
                    Detail <span className="font-bold">Pengajuan Bon Sementara</span>
                </h2>
                <div className="bg-white dark:bg-gray-800 p-4 md:p-6 rounded-lg mb-6 shadow-sm transition-colors">
                    {/* Desktop View (xl:1280px and above) */}
                    <div className="hidden xl:block">
                        <div className="grid grid-cols-2 gap-x-16 mb-4 font-medium">
                            <div className="grid grid-cols-[auto_1fr] gap-x-16">
                                {[...Array(4)].map((_, index) => (
                                    <React.Fragment key={`desktop-left-${index}`}>
                                        <Skeleton width={120} height={24} />
                                        <Skeleton width={200} height={24} />
                                    </React.Fragment>
                                ))}
                            </div>
                            <div className="grid grid-cols-[auto_1fr] gap-x-16">
                                {[...Array(4)].map((_, index) => (
                                    <React.Fragment key={`desktop-right-${index}`}>
                                        <Skeleton width={120} height={24} />
                                        <Skeleton width={200} height={24} />
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Tablet/Laptop View (768px - 1279px) */}
                    <div className="hidden md:block xl:hidden">
                        <div className="grid grid-cols-2 gap-x-8 mb-4">
                            <div className="space-y-1">
                                {[...Array(4)].map((_, index) => (
                                    <div key={`tablet-left-${index}`} className="flex items-center">
                                        <Skeleton width={100} height={20} className="mr-2" />
                                        <Skeleton width={150} height={20} />
                                    </div>
                                ))}
                            </div>
                            <div className="space-y-1">
                                {[...Array(4)].map((_, index) => (
                                    <div key={`tablet-right-${index}`} className="flex items-center">
                                        <Skeleton width={100} height={20} className="mr-2" />
                                        <Skeleton width={150} height={20} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Mobile Vieww (below 768px) */}
                    <div className="md:hidden">
                        <div className="space-y-1 mb-4">
                            {[...Array(8)].map((_, index) => (
                                <div
                                    key={`mobile-${index}`}
                                    className="grid grid-cols-[120px_1fr] gap-x-1 text-sm items-start"
                                >
                                    <Skeleton width={100} height={20} />
                                    <Skeleton width={'100%'} height={20} />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Responsive Table Skeleton */}
                    <div className="overflow-x-auto -mx-4 md:mx-0 mb-8">
                        <div className="min-w-[640px] md:w-full p-4 md:p-0">
                            {/* Table Header Skeleton */}
                            <div className="bg-gray-100 dark:bg-gray-700 grid grid-cols-5 rounded-t-lg">
                                {[...Array(5)].map((_, index) => (
                                    <div key={`header-${index}`} className="p-2">
                                        <Skeleton height={24} />
                                    </div>
                                ))}
                            </div>

                            {/* Table Body Skeleton */}
                            {[...Array(1)].map((_, rowIndex) => (
                                <div key={`row-${rowIndex}`} className="grid grid-cols-5 border-b dark:border-gray-600">
                                    {[...Array(5)].map((_, colIndex) => (
                                        <div key={`cell-${rowIndex}-${colIndex}`} className="p-2">
                                            <Skeleton height={20} />
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Action Buttons Skeleton */}
                    <div className="flex flex-col md:flex-row md:justify-end space-y-2 md:space-y-0 md:space-x-2 mt-4">
                        <div className="w-full md:w-[170px]">
                            <Skeleton height={45} className="w-full" />
                        </div>
                        <div className="w-full md:w-[170px]">
                            <Skeleton height={45} className="w-full" />
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="container mx-auto py-10 md:py-8">
            <DetailBackButton />
            <h2 className="text-xl font-medium mb-4 dark:text-gray-100">
                Detail <span className="font-bold">Pengajuan Bon Sementara</span>
            </h2>

            <div className="bg-white dark:bg-gray-800 p-4 md:p-6 rounded-lg shadow transition-colors">
                {/* Responsive grid for user details */}
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 xl:gap-x-16 mb-6 font-medium">
                    {/* Mobile dan Tablet view (up to xl breakpoint) */}
                    <div className="xl:hidden">
                        <div className="flex flex-wrap justify-between gap-1 md:gap-x-12">
                            {/* First Column */}
                            <div className="space-y-1 flex-1">
                                <div className="grid grid-cols-[120px_auto_1fr] gap-x-1 text-sm items-start">
                                    <p>Nama Lengkap</p>
                                    <p className="text-left">:</p>
                                    <p className="break-words">{bonSementaraDetail?.user?.nama ?? 'N/A'}</p>
                                </div>
                                <div className="grid grid-cols-[120px_auto_1fr] gap-x-1 text-sm items-start">
                                    <p>Department</p>
                                    <p className="text-left">:</p>
                                    <p className="break-words">
                                        {Array.isArray(bonSementaraDetail?.user?.department) &&
                                            bonSementaraDetail.user.department.length > 0
                                            ? bonSementaraDetail.user.department.join(', ')
                                            : ''}
                                    </p>
                                </div>
                                <div className="grid grid-cols-[120px_auto_1fr] gap-x-1 text-sm items-start">
                                    <p>Unit Bisnis</p>
                                    <p className="text-left">:</p>
                                    <p className="break-words">{bonSementaraDetail?.user?.unit ?? 'N/A'}</p>
                                </div>
                                <div className="grid grid-cols-[120px_auto_1fr] gap-x-1 text-sm items-start">
                                    <p>Posisi</p>
                                    <p className="text-left">:</p>
                                    <p className="break-words">{bonSementaraDetail?.user?.posisi ?? 'N/A'}</p>
                                </div>
                            </div>

                            {/* Second column */}
                            <div className="space-y-1 flex-1">
                                <div className="grid grid-cols-[120px_auto_1fr] gap-x-1 text-sm items-start">
                                    <p>Nama Bank</p>
                                    <p className="text-left">:</p>
                                    <p className="break-words">{bonSementaraDetail?.user?.bankName ?? 'N/A'}</p>
                                </div>
                                <div className="grid grid-cols-[120px_auto_1fr] gap-x-1 text-sm items-start">
                                    <p>No. Rekening</p>
                                    <p className="text-left">:</p>
                                    <p className="break-all">{bonSementaraDetail?.user?.accountNumber ?? 'N/A'}</p>
                                </div>

                                <div className="grid grid-cols-[120px_auto_1fr] gap-x-1 text-sm items-start">
                                    <p>Status</p>
                                    <p className="text-left">:</p>
                                    <p className="break-words">{renderStatus(getStatusWithNextReviewer(bonSementaraDetail, reviewers))}</p>
                                </div>
                                <div className="grid grid-cols-[120px_auto_1fr] gap-x-1 text-sm items-start">
                                    <p>
                                        {bonSementaraDetail?.status === 'Ditolak'
                                            ? 'Ditolak Oleh'
                                            : 'Disetujui Oleh'}
                                    </p>
                                    <p className="text-left">:</p>
                                    <p className="break-words">
                                        {getDetailedApprovalStatus(bonSementaraDetail, reviewers)}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Desktop layout  */}
                    <div className="hidden xl:grid grid-cols-[auto_1fr] gap-x-16 text-base">
                        <p>Nama Lengkap</p>
                        <p>: {bonSementaraDetail?.user?.nama ?? 'N/A'}</p>
                        <p>Department</p>
                        <p>
                            :{' '}
                            {Array.isArray(bonSementaraDetail?.user?.department) &&
                                bonSementaraDetail.user.department.length > 0
                                ? bonSementaraDetail.user.department.join(', ')
                                : ''}
                        </p>
                        <p>Unit Bisnis</p>
                        <p>: {bonSementaraDetail?.user?.unit ?? 'N/A'}</p>
                        <p>Posisi</p>
                        <p>: {bonSementaraDetail?.user?.posisi ?? 'N/A'}</p>
                    </div>
                    <div className="hidden xl:grid grid-cols-[auto_1fr] gap-x-16 text-base">
                        <p>Nama Bank</p>
                        <p>: {bonSementaraDetail?.user?.bankName ?? 'N/A'}</p>
                        <p>Nomor Rekening</p>
                        <p>: {bonSementaraDetail?.user?.accountNumber ?? 'N/A'}</p>
                        <p>Status</p>
                        <p>: {renderStatus(getStatusWithNextReviewer(bonSementaraDetail, reviewers))}</p>
                        <p>
                            {bonSementaraDetail?.status === 'Ditolak'
                                ? 'Ditolak Oleh'
                                : 'Disetujui Oleh'}
                        </p>
                        <p>: {getDetailedApprovalStatus(bonSementaraDetail, reviewers)}</p>
                    </div>
                </div>

                {/* Bagian AN: Riwayat Edit -- hanya tampil kalau pengajuan ini
                    pernah diedit ADMIN setelah dibuat (isEditMode di form). */}
                {editHistoryWithNames.length > 0 && (
                    <div className="mb-8 p-4 border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                        <p className="font-medium text-amber-800 dark:text-amber-300 mb-2">
                            Riwayat Edit ({editHistoryWithNames.length}x)
                        </p>
                        <ul className="space-y-2 text-sm">
                            {editHistoryWithNames.map((entry, index) => (
                                <li key={index} className="text-gray-700 dark:text-gray-200">
                                    <span className="font-medium">
                                        {new Date(entry.timestamp).toLocaleString('id-ID', {
                                            day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                        })}
                                    </span>
                                    {' -- '}{entry.actorName} ({entry.status.replace('Data Diubah oleh ', '')})
                                    {entry.reason && (
                                        <span className="block text-gray-600 dark:text-gray-400 italic">"{entry.reason}"</span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Responsive table wrapper */}
                <div className="mb-8 overflow-x-auto -mx-4 md:mx-0">
                    <div className="min-w-[640px] md:w-full p-4 md:p-0">
                        <table className="w-full bg-white dark:bg-gray-800 border dark:border-gray-600 rounded-lg text-sm dark:text-gray-200">
                            <thead>
                                <tr className="bg-gray-100 dark:bg-gray-700 text-left dark:text-gray-100">
                                    <th className="px-4 py-2 border dark:border-gray-600 w-auto">Nomor BS</th>
                                    <th className="px-4 py-2 border dark:border-gray-600 w-auto">Kategori</th>
                                    <th className="px-4 py-2 border dark:border-gray-600 w-auto">Aktivitas</th>
                                    <th className="px-4 py-2 border dark:border-gray-600 w-auto">Jumlah BS</th>
                                    <th className="px-4 py-2 border dark:border-gray-600 w-auto">Tanggal Pengajuan</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bonSementaraDetail?.bonSementara?.map((item, index) => (
                                    <tr key={index}>
                                        <td className="px-4 py-2 border dark:border-gray-600">{item.nomorBS}</td>
                                        <td className="px-4 py-2 border dark:border-gray-600">{item.kategori}</td>
                                        <td className="px-4 py-2 border dark:border-gray-600 whitespace-pre-wrap">{item.aktivitas}</td>
                                        <td className="px-4 py-2 border dark:border-gray-600">Rp{item.jumlahBS.toLocaleString('id-ID')}</td>
                                        <td className="px-4 py-2 border dark:border-gray-600">
                                            {formatDate(bonSementaraDetail.tanggalPengajuan) ?? 'N/A'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                {(bonSementaraDetail?.status === 'Dibatalkan' || bonSementaraDetail?.status === 'Ditolak') && (
                                    <>
                                        <tr>
                                            <td colSpan="5" className="px-4 py-2 text-left border dark:border-gray-600">
                                                &nbsp;
                                            </td>
                                        </tr>
                                        <tr>
                                            <td colSpan="5" className="px-4 py-2 text-left border dark:border-gray-600">
                                                <span className="font-semibold">
                                                    {bonSementaraDetail?.status === 'Dibatalkan' ? 'Alasan Pembatalan :' : 'Alasan Penolakan :'}
                                                </span>{' '}
                                                {bonSementaraDetail?.status === 'Dibatalkan'
                                                    ? bonSementaraDetail?.cancelReason
                                                    : bonSementaraDetail?.rejectReason}
                                            </td>
                                        </tr>
                                    </>
                                )}
                            </tfoot>
                        </table>
                    </div>
                </div>

                {/* Bagian CA: nota yang sudah dicatat di "Catat Nota" untuk BS ini --
                    siap dirangkai jadi baris item LPJ lewat tombol "Buat Laporan". */}
                {userData?.uid === bonSementaraDetail?.user.uid && notaDraftForBs.length > 0 && !isLpjApproved && (
                    <div className="mt-6 flex items-center justify-between gap-3 flex-wrap bg-blue-50 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-700 rounded-lg px-4 py-3">
                        <div className="text-sm text-gray-700 dark:text-gray-200">
                            <span className="font-semibold">{notaDraftForBs.length} nota sudah tercatat</span> untuk BS ini (total {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(notaDraftForBs.reduce((s, n) => s + (n.jumlahBiaya || 0), 0))}) -- siap jadi baris item LPJ, klik "Buat Laporan".
                        </div>
                    </div>
                )}

                {/* Responsive action buttons */}
                <div className="flex flex-col md:flex-row md:justify-end mt-6 space-y-2 md:space-y-0 md:space-x-2">
                    <DetailApprovalActions docType="bs" item={bonSementaraDetail ? { ...bonSementaraDetail, id } : null} />

                    {/* Bagian BO: lampiran hanya ada untuk BS > Rp3 juta. */}
                    {bonSementaraDetail?.lampiranUrl && (
                        <button
                            className="w-full md:w-auto px-12 py-3 rounded text-red-600 bg-transparent hover:text-red-800 border border-red-600 hover:border-red-800"
                            onClick={() => {
                                setModalPdfUrl(bonSementaraDetail.lampiranUrl)
                                setModalTitle(`Lampiran ${bonSementaraDetail.displayId}`)
                            }}
                        >
                            Lihat Lampiran
                        </button>
                    )}

                    {userData?.uid === bonSementaraDetail?.user.uid && (
                        <div className="flex flex-col items-end">
                            <button
                                onClick={handleBuatLaporan}
                                className={`w-full md:w-auto px-12 py-3 rounded ${bonSementaraDetail?.status === 'Disetujui' && !isLpjApproved
                                    ? 'text-red-600 bg-transparent hover:text-red-800 border border-red-600 hover:border-red-800'
                                    : 'text-gray-400 dark:text-gray-600 bg-transparent border border-gray-200 dark:border-gray-600 cursor-not-allowed'
                                    }`}
                                disabled={bonSementaraDetail?.status !== 'Disetujui' || isLpjApproved}
                            >
                                Buat Laporan
                            </button>
                            {isLpjApproved && (
                                <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 mt-1">
                                    Reported -- LPJ sudah Disetujui
                                </span>
                            )}
                        </div>
                    )}

                    {(userData?.uid === bonSementaraDetail?.user.uid || 
                    bonSementaraDetail?.user.reviewer1?.includes(userData?.uid) ||
                    bonSementaraDetail?.user.reviewer2?.includes(userData?.uid) ||
                    userData?.role === 'Super Admin') && (
                        <button
                            className={`px-16 py-3 rounded ${bonSementaraDetail?.status === 'Disetujui'
                                ? 'text-white bg-red-600 hover:bg-red-700 hover:text-gray-200'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                }`}
                            onClick={handleGenerateAndPreviewPDF}
                            disabled={bonSementaraDetail?.status !== 'Disetujui' || isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} className="mr-1 animate-spin" />
                                    Loading..
                                </>
                            ) : (
                                'Print'
                            )}
                        </button>
                    )}
                </div>
            </div>

            <ModalPDF showModal={!!modalPdfUrl} previewUrl={modalPdfUrl} onClose={closePreview} title={modalTitle} />

        </div>
    )
}

export default DetailBs